'use client';

import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button, Checkbox, Drawer, IconButton, Select, TextArea, TextField } from '@/shared/components/ui/common';
import { useToast } from '@/shared/providers';
import { getErrorMessage } from '@/shared/utils/error';
import { useAppDispatch } from '@/store/hooks';
import { atualizarEscritoriosSituacaoArbitral, criarSituacaoArbitral, editarSituacaoArbitral } from '../store';
import type { EscritorioOpcao, SituacaoArbitral } from '../types/situacaoArbitralTypes';
import { STATUS_PAI_OPCOES } from '../types/situacaoArbitralTypes';
import { escritoriosMudaram } from '../utils/situacaoArbitralUtils';
const STATUS_PAI_NENHUM = 'NENHUM';
interface SituacaoFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  situacao?: SituacaoArbitral | null;
  escritorios: EscritorioOpcao[];
}
export function SituacaoFormDrawer({
  isOpen,
  onClose,
  situacao,
  escritorios
}: SituacaoFormDrawerProps) {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [statusPai, setStatusPai] = useState<string>(STATUS_PAI_NENHUM);
  const [escritorioIds, setEscritorioIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (situacao) {
      setLabel(situacao.label);
      setDescription(situacao.description ?? '');
      setStatusPai(situacao.statusPai ?? STATUS_PAI_NENHUM);
      setEscritorioIds(situacao.escritorioIds);
    } else {
      setLabel('');
      setDescription('');
      setStatusPai(STATUS_PAI_NENHUM);
      setEscritorioIds([]);
    }
    setError('');
    setLoading(false);
  }, [situacao, isOpen]);
  const isEdicao = Boolean(situacao?.id);
  const podeSalvar = label.trim().length >= 2;
  const toggleEscritorio = (id: string) => {
    setEscritorioIds(anterior => anterior.includes(id) ? anterior.filter(item => item !== id) : [...anterior, id]);
  };
  const salvar = async () => {
    if (!podeSalvar) {
      setError('Informe um nome com pelo menos 2 caracteres.');
      return;
    }
    setLoading(true);
    setError('');
    const statusPaiValor = statusPai === STATUS_PAI_NENHUM ? null : statusPai;
    try {
      if (situacao?.id) {
        await dispatch(editarSituacaoArbitral({
          id: situacao.id,
          data: {
            label: label.trim(),
            description: description.trim() || null,
            statusPai: statusPaiValor
          }
        })).unwrap();
        if (escritoriosMudaram(situacao.escritorioIds, escritorioIds)) {
          try {
            await dispatch(atualizarEscritoriosSituacaoArbitral({
              id: situacao.id,
              escritorioIds
            })).unwrap();
          } catch (err_0) {
            const mensagem_0 = getErrorMessage(err_0);
            setError(mensagem_0);
            toast.showError('Situação salva, mas as câmaras não foram atualizadas', mensagem_0);
            return;
          }
        }
        toast.showSuccess('Situação atualizada', `"${label.trim()}" foi salva.`);
      } else {
        await dispatch(criarSituacaoArbitral({
          label: label.trim(),
          description: description.trim() || null,
          statusPai: statusPaiValor,
          escritorioIds
        })).unwrap();
        toast.showSuccess('Situação criada', `"${label.trim()}" foi adicionada.`);
      }
      onClose();
    } catch (err) {
      const mensagem = getErrorMessage(err);
      setError(mensagem);
      toast.showError(isEdicao ? 'Erro ao atualizar situação' : 'Erro ao criar situação', mensagem);
    } finally {
      setLoading(false);
    }
  };
  return <Drawer open={isOpen} onOpenChange={onClose}>
      <Drawer.Content className="max-w-xl">
        <div className="flex h-full w-full flex-col">
          <div className="flex flex-none items-center justify-between border-b border-neutral-200 px-6 py-4">
            <div className="flex flex-col">
              <span className="text-heading-3 font-heading-3 text-default-font">
                {isEdicao ? 'Editar situação' : 'Nova situação'}
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Situações são substatus informativos — mostram o que o processo está aguardando.
              </span>
            </div>
            <IconButton icon={<X className="h-5 w-5" />} onClick={onClose} variant="neutral-tertiary" />
          </div>

          <div className="flex grow flex-col gap-6 overflow-y-auto p-6">
            {error && <div className="border-error-200 bg-error-50 text-error-700 rounded-[8px] border border-solid p-3 text-sm">
                {error}
              </div>}

            <TextField label="Nome" className="w-full">
              <TextField.Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Ex: Aguardando pagamento" />
            </TextField>

            {isEdicao && situacao?.key && <TextField label="Chave (imutável)" className="w-full">
                <TextField.Input value={situacao.key} disabled readOnly />
              </TextField>}

            <TextArea label="Descrição (opcional)" className="w-full">
              <TextArea.Input value={description} onChange={e_0 => setDescription(e_0.target.value)} placeholder="Explique quando esta situação se aplica." />
            </TextArea>

            <div className="flex flex-col gap-1">
              <Select label="Status relacionado" value={statusPai} onValueChange={valor => setStatusPai(valor)} className="w-full">
                <Select.Item value={STATUS_PAI_NENHUM}>Transversal (nenhum)</Select.Item>
                {STATUS_PAI_OPCOES.map(opcao => <Select.Item key={opcao.value} value={opcao.value}>
                    {opcao.label}
                  </Select.Item>)}
              </Select>
              <span className="text-caption font-caption text-subtext-color">
                A qual etapa esta situação pertence. Transversal = vale para qualquer etapa.
              </span>
            </div>

            <div className="flex flex-col gap-3">
              <span className="text-caption-bold font-caption-bold text-default-font">Câmaras</span>
              <span className="text-caption font-caption text-subtext-color">
                Nenhuma marcada = disponível para todas as câmaras (global).
              </span>
              {escritorios.length === 0 ? <span className="text-caption font-caption text-subtext-color">
                  Nenhuma câmara disponível.
                </span> : <div className="flex flex-col gap-2">
                  {escritorios.map(escritorio => <Checkbox key={escritorio.id} label={escritorio.nome} checked={escritorioIds.includes(escritorio.id)} onCheckedChange={() => toggleEscritorio(escritorio.id)} />)}
                </div>}
            </div>
          </div>

          <div className="flex flex-none items-center justify-end gap-3 border-t border-neutral-200 px-6 py-4">
            <Button variant="neutral-secondary" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={salvar} loading={loading} disabled={!podeSalvar}>
              {isEdicao ? 'Salvar' : 'Criar situação'}
            </Button>
          </div>
        </div>
      </Drawer.Content>
    </Drawer>;
}